// The durable BINARY queue for floor evidence photos (spec 04 §2.11 / §3.3
// step 10, D-096) — the sibling of `outbox-store.ts`, in the SAME IndexedDB
// database (`assessly.floor`, v2), in its own object store.
//
// WHY BLOBS GET THEIR OWN STORE instead of riding inside the outbox record:
// the outbox record is the FLAG — small JSON, written/rewritten on every retry
// state change. A 10 MB camera JPEG rewritten on every backoff tick would turn
// each retry into a multi-megabyte IndexedDB write on a cheap tablet. So the
// bytes are written ONCE at capture, keyed by their own tap-time uuid v7, and
// the flag references them by id (`OutboxItem.photoIds`). Spec 18 §2.9.2
// specifies the identical shape for the P3 sync protocol (`blobs` beside
// `mutations`, `depends_on_blob_ids`); this is that design landed early, on the
// one screen that needs it now.
//
// WHAT A PHOTO RECORD REMEMBERS is the resumable state of the three-call
// upload (`POST /v1/files` → presigned PUT → `POST /v1/files/{id}/store`):
//
//   fileId      — set (durably, BEFORE the PUT is attempted) as soon as call 1
//                 answers. This is the replay spine: a process death after
//                 call 1 must resume with the SAME file id, or every retry
//                 would mint a fresh registry row and the flag could ship an id
//                 the bytes never reached. Calls 2 and 3 are idempotent per the
//                 files contract, so resuming from a persisted fileId is safe
//                 at any point.
//   uploadUrl / uploadExpiresAtMs — the presigned PUT and its deadline. An
//                 expired presign cannot be refreshed for the same file id (the
//                 registry mints URL and row together), so expiry resets the
//                 record to "no file yet" and call 1 runs again — the orphaned
//                 `pending` row is inert (never `stored`, so never attachable
//                 and never resolvable) and is the recorded cost of that
//                 design (D-096).
//   uploaded / storedFileId — the PUT confirmed, and call 3 confirmed. Once
//                 `storedFileId` is set the id is FINAL: it is what the flag's
//                 `photo_file_ids` will carry on every send and every replay.
//
// DURABILITY RULES ARE THE OUTBOX'S OWN: `put()` resolves only when the
// transaction completes; a queued photo survives app restart and airplane mode
// (that is the whole point); the memory fallback exists so a locked-down
// WebView still works, and its non-durability is surfaced through the same
// `durable` flag the outbox chrome already renders.

import {
  isDeadConnection,
  openIndexedDb,
  PHOTO_STORE_NAME,
  type IdbFactoryLike,
} from "./outbox-store";

/** The three floor evidence purposes (D-064 pt 8 / D-096; `quality_evidence`
 * added by slice 07-A). The registry refuses a photo consumed under the wrong
 * purpose — module 05's ledger only accepts `waste_evidence` ids (spec 05
 * §2.3) and module 07's result applier only `quality_evidence` ones (spec 07
 * §2.6), so the purpose must ride the RECORD from capture, not be assumed at
 * upload. */
export type FloorEvidencePurpose = "production_evidence" | "waste_evidence" | "quality_evidence";

/** The purposes a persisted record may claim. Kept beside the type so a fourth
 * purpose cannot be added to one and forgotten in the other — which is exactly
 * how `waste_evidence` was dropped on read in 2026-08. */
const FLOOR_EVIDENCE_PURPOSES: readonly FloorEvidencePurpose[] = [
  "production_evidence",
  "waste_evidence",
  "quality_evidence",
];

function parseFloorEvidencePurpose(value: unknown): FloorEvidencePurpose | undefined {
  return (FLOOR_EVIDENCE_PURPOSES as readonly unknown[]).includes(value)
    ? (value as FloorEvidencePurpose)
    : undefined;
}

/** One captured evidence photo, durable on the device until its flag settles. */
export interface PhotoRecord {
  /** uuid v7, minted at CAPTURE time. Primary key; what the flag references. */
  readonly photoId: string;
  /** The registry purpose call 1 declares. OPTIONAL because v2 records queued
   * before this field existed carry none — the uploader falls back to
   * `production_evidence`, which is what every pre-field record was
   * (2026-08-09: the S8 waste capture used to register its evidence as
   * `production_evidence`, and module 05's ledger then refused the id — the
   * capture parked on a purpose mismatch the operator could never fix). */
  readonly purpose?: FloorEvidencePurpose;
  /** The camera bytes, verbatim — never re-encoded (D-096: the server's 20 MB
   * photo cap clears real phone cameras, and a lossy client re-encode would
   * make the stored sha the hash of something the camera never shot). */
  readonly bytes: Blob;
  readonly mime: string;
  readonly byteSize: number;
  /** Lowercase hex SHA-256 of `bytes`, computed at capture — what call 1
   * declares and call 3 verifies. */
  readonly sha256: string;
  /** Epoch ms at capture (device clock). */
  readonly capturedAt: number;
  /** Who captured it (floor-principal string) — same hold-if-foreign rule as
   * the outbox records that reference it. */
  readonly principal: string | null;
  /** Set after `POST /v1/files` answered (call 1). Null = not yet created. */
  readonly fileId: string | null;
  /** The presigned PUT from call 1, with its deadline. */
  readonly uploadUrl: string | null;
  readonly uploadExpiresAtMs: number | null;
  /** The PUT (call 2) confirmed 2xx. */
  readonly uploaded: boolean;
  /** Set after `POST /v1/files/{id}/store` (call 3) confirmed `stored`. FINAL:
   * this exact id rides the flag on every attempt. */
  readonly storedFileId: string | null;
  /**
   * `Idempotency-Key`s for calls 1 and 3 — both are platform mutations, and
   * the platform idempotency store replays STORED responses (errors included)
   * under a reused key. So each key is persisted (a transport-lost success
   * replays to the SAME answer — same file id, no duplicate row) and ROTATED
   * by the uploader whenever the server actually REFUSED under it (a refusal
   * replayed forever would wedge the retry path).
   */
  readonly createKey: string;
  readonly storeKey: string;
}

export interface PhotoStore {
  get(photoId: string): Promise<PhotoRecord | undefined>;
  /** Insert or replace by `photoId`. Resolves only once durable. */
  put(record: PhotoRecord): Promise<void>;
  /** Idempotent delete. */
  remove(photoId: string): Promise<void>;
  readAll(): Promise<PhotoRecord[]>;
  /** Device hand-over / sign-out. */
  clear(): Promise<void>;
}

// ---------------------------------------------------------------------------
// In-memory adapter (tests + the IndexedDB-less fallback)
// ---------------------------------------------------------------------------

export function memoryPhotoStore(): PhotoStore {
  const rows = new Map<string, PhotoRecord>();
  return {
    get: (photoId) => Promise.resolve(rows.get(photoId)),
    put: (record) => {
      rows.set(record.photoId, record);
      return Promise.resolve();
    },
    remove: (photoId) => {
      rows.delete(photoId);
      return Promise.resolve();
    },
    readAll: () => Promise.resolve([...rows.values()]),
    clear: () => {
      rows.clear();
      return Promise.resolve();
    },
  };
}

// ---------------------------------------------------------------------------
// IndexedDB adapter — same durability discipline as the outbox adapter
// ---------------------------------------------------------------------------

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error("indexeddb request failed"));
    };
  });
}

/** Durable = the TRANSACTION completed (outbox-store.ts's rule, same reasons). */
function transactionDone(txn: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    txn.oncomplete = () => {
      resolve();
    };
    txn.onerror = () => {
      reject(txn.error ?? new Error("indexeddb transaction failed"));
    };
    txn.onabort = () => {
      reject(txn.error ?? new Error("indexeddb transaction aborted"));
    };
  });
}

/** Read a persisted row back, refusing anything malformed (a corrupt row must
 * not crash the queue — its flag will fail photo resolution loudly instead). */
export function photoFromRecord(value: unknown): PhotoRecord | undefined {
  if (value === null || typeof value !== "object") return undefined;
  const record = value as Record<string, unknown>;
  const photoId = record["photoId"];
  const bytes = record["bytes"];
  const mime = record["mime"];
  const sha256 = record["sha256"];
  if (typeof photoId !== "string" || photoId.length === 0) return undefined;
  if (!(bytes instanceof Blob)) return undefined;
  if (typeof mime !== "string" || mime.length === 0) return undefined;
  if (typeof sha256 !== "string" || sha256.length === 0) return undefined;
  const createKey = record["createKey"];
  const storeKey = record["storeKey"];
  if (typeof createKey !== "string" || createKey.length === 0) return undefined;
  if (typeof storeKey !== "string" || storeKey.length === 0) return undefined;
  const str = (key: string): string | null => {
    const v = record[key];
    return typeof v === "string" && v.length > 0 ? v : null;
  };
  const num = (key: string): number | null => {
    const v = record[key];
    return typeof v === "number" && Number.isFinite(v) ? v : null;
  };
  // The purpose survives the round-trip EXPLICITLY (2026-08-09): this
  // re-materializer lists every field it keeps, so when `purpose` was added
  // to the record it was silently DROPPED on read — the uploader then fell
  // back to production_evidence and module 05's ledger refused the id. Only
  // the KNOWN purposes pass; anything else reads as absent and takes the
  // uploader's legacy fallback. The allow-list is `FLOOR_EVIDENCE_PURPOSES`
  // rather than an inline literal pair precisely because the inline pair is
  // what went stale last time: slice 07-A's `quality_evidence` would have been
  // dropped here on every reload and every airplane-mode shift, and the result
  // would have parked on a `QUALITY_EVIDENCE_INVALID` no operator could fix.
  const purpose = parseFloorEvidencePurpose(record["purpose"]);
  return {
    photoId,
    ...(purpose !== undefined ? { purpose } : {}),
    bytes,
    mime,
    byteSize: num("byteSize") ?? bytes.size,
    sha256,
    capturedAt: num("capturedAt") ?? 0,
    principal: str("principal"),
    fileId: str("fileId"),
    uploadUrl: str("uploadUrl"),
    uploadExpiresAtMs: num("uploadExpiresAtMs"),
    uploaded: record["uploaded"] === true,
    storedFileId: str("storedFileId"),
    createKey,
    storeKey,
  };
}

export function indexedDbPhotoStore(db: IDBDatabase): PhotoStore {
  const write = async (apply: (store: IDBObjectStore) => void): Promise<void> => {
    const txn = db.transaction(PHOTO_STORE_NAME, "readwrite");
    const done = transactionDone(txn);
    apply(txn.objectStore(PHOTO_STORE_NAME));
    await done;
  };
  return {
    get: async (photoId) => {
      const txn = db.transaction(PHOTO_STORE_NAME, "readonly");
      const row = await promisify<unknown>(
        txn.objectStore(PHOTO_STORE_NAME).get(photoId) as IDBRequest<unknown>,
      );
      return photoFromRecord(row);
    },
    put: (record) => write((store) => void store.put(record)),
    remove: (photoId) => write((store) => void store.delete(photoId)),
    readAll: async () => {
      const txn = db.transaction(PHOTO_STORE_NAME, "readonly");
      const rows = await promisify<unknown[]>(
        txn.objectStore(PHOTO_STORE_NAME).getAll() as IDBRequest<unknown[]>,
      );
      const parsed: PhotoRecord[] = [];
      for (const row of rows) {
        const record = photoFromRecord(row);
        if (record !== undefined) parsed.push(record);
      }
      return parsed;
    },
    clear: () => write((store) => void store.clear()),
  };
}

/** The shipping adapter: owns (and reopens) its connection, like the outbox's. */
export function reopeningPhotoStore(factory: IdbFactoryLike): PhotoStore {
  let db: IDBDatabase | undefined;
  let opening: Promise<IDBDatabase> | undefined;

  const connect = async (): Promise<IDBDatabase> => {
    if (db !== undefined) return db;
    opening ??= openIndexedDb(factory)
      .then((opened) => {
        opened.onclose = () => {
          db = undefined;
        };
        opened.onversionchange = () => {
          opened.close();
          db = undefined;
        };
        db = opened;
        return opened;
      })
      .finally(() => {
        opening = undefined;
      });
    return opening;
  };

  const attempt = async <T>(run: (store: PhotoStore) => Promise<T>): Promise<T> => {
    const handle = await connect();
    try {
      return await run(indexedDbPhotoStore(handle));
    } catch (error) {
      if (!isDeadConnection(error)) throw error;
      db = undefined;
      const reopened = await connect();
      return run(indexedDbPhotoStore(reopened));
    }
  };

  return {
    get: (photoId) => attempt((store) => store.get(photoId)),
    put: (record) => attempt((store) => store.put(record)),
    remove: (photoId) => attempt((store) => store.remove(photoId)),
    readAll: () => attempt((store) => store.readAll()),
    clear: () => attempt((store) => store.clear()),
  };
}

export interface OpenedPhotoStore {
  readonly store: PhotoStore;
  /** False = memory fallback; the chrome's existing non-durable warning covers
   * the photo queue too, because it is the same database that could not open. */
  readonly durable: boolean;
}

export async function openPhotoStore(
  factory: IdbFactoryLike | undefined = typeof indexedDB === "undefined" ? undefined : indexedDB,
): Promise<OpenedPhotoStore> {
  if (factory === undefined) return { store: memoryPhotoStore(), durable: false };
  try {
    const probe = await openIndexedDb(factory);
    probe.close();
    return { store: reopeningPhotoStore(factory), durable: true };
  } catch {
    return { store: memoryPhotoStore(), durable: false };
  }
}
